import React, { useState } from 'react';
import { useProducts } from '../context/ProductContext';
import aiAutoTrainingService from '../services/aiAutoTrainingService';
import {
  Plus,
  Trash2,
  Edit,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Zap,
  Brain,
  Database
} from 'lucide-react';

const AutoTrainingDemo = () => {
  const { products, addProduct, updateProduct, deleteProduct } = useProducts();
  const [isAdding, setIsAdding] = useState(false);
  const [message, setMessage] = useState(null);
  const [autoTrainingStats, setAutoTrainingStats] = useState({});

  // Sample product for testing
  const sampleProduct = {
    name: 'Test Product - Auto Training Demo',
    price: 'K999',
    category: 'Test Category',
    description: 'This is a test product to demonstrate automatic AI training when products are added.',
    inStock: true,
    featured: false,
    visible: true
  };

  // Show message
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  // Add test product
  const handleAddTestProduct = async () => {
    setIsAdding(true);
    try {
      const newProduct = {
        ...sampleProduct,
        id: Date.now(),
        name: `Test Product ${Date.now()}`
      };

      // Add product (this should trigger auto training)
      await addProduct(newProduct);
      
      showMessage('✅ Test product added! AI should automatically retrain within 30 seconds.', 'success');
      
      // Update stats
      setTimeout(() => {
        setAutoTrainingStats(aiAutoTrainingService.getStats());
      }, 1000);
      
    } catch (error) {
      console.error('Error adding test product:', error);
      showMessage('❌ Failed to add test product', 'error');
    } finally {
      setIsAdding(false);
    }
  };

  // Remove test products
  const handleRemoveTestProducts = async () => {
    try {
      const testProducts = products.filter(p => p.name.includes('Test Product'));
      
      for (const product of testProducts) {
        await deleteProduct(product.id);
      }
      
      showMessage(`✅ Removed ${testProducts.length} test products! AI should automatically retrain.`, 'success');
      
      // Update stats
      setTimeout(() => {
        setAutoTrainingStats(aiAutoTrainingService.getStats());
      }, 1000);
      
    } catch (error) {
      console.error('Error removing test products:', error);
      showMessage('❌ Failed to remove test products', 'error');
    }
  };

  // Get current stats
  const refreshStats = () => {
    setAutoTrainingStats(aiAutoTrainingService.getStats());
    showMessage('📊 Stats refreshed', 'info');
  };

  // Listen for auto training events
  React.useEffect(() => {
    const handleAutoTrainingComplete = (event) => {
      console.log('🎉 Auto training completed in demo:', event.detail);
      setAutoTrainingStats(aiAutoTrainingService.getStats());
      showMessage('🤖 AI automatically retrained with new product data!', 'success');
    };

    window.addEventListener('aiTrainingComplete', handleAutoTrainingComplete);
    
    // Get initial stats
    setAutoTrainingStats(aiAutoTrainingService.getStats());

    return () => {
      window.removeEventListener('aiTrainingComplete', handleAutoTrainingComplete);
    };
  }, []);

  const testProducts = products.filter(p => p.name.includes('Test Product'));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Auto Training Demo</h1>
          <p className="text-gray-600 mt-1">Test automatic AI training when product data changes</p>
        </div>
        <button
          onClick={refreshStats}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center transition-colors"
        >
          <RefreshCw size={16} className="mr-2" />
          Refresh Stats
        </button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : message.type === 'error'
            ? 'bg-red-50 text-red-800 border border-red-200'
            : 'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 mr-2" />
          ) : message.type === 'error' ? (
            <AlertCircle className="w-5 h-5 mr-2" />
          ) : (
            <RefreshCw className="w-5 h-5 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Auto Training Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Auto Training Status</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Training Sessions</p>
                <p className="text-2xl font-bold text-purple-900">{autoTrainingStats.totalTrainingSessions || 0}</p>
              </div>
              <Brain className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Success Rate</p>
                <p className="text-2xl font-bold text-green-900">{autoTrainingStats.successRate?.toFixed(1) || 100}%</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Queue Length</p>
                <p className="text-2xl font-bold text-blue-900">{autoTrainingStats.queueLength || 0}</p>
              </div>
              <Database className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Auto Training</p>
                <p className="text-lg font-bold text-yellow-900">
                  {autoTrainingStats.autoTrainingEnabled ? 'ON' : 'OFF'}
                </p>
              </div>
              <Zap className="w-8 h-8 text-yellow-500" />
            </div>
          </div>
        </div>

        {autoTrainingStats.lastAutoUpdate && (
          <div className="text-sm text-gray-600">
            Last auto update: {new Date(autoTrainingStats.lastAutoUpdate).toLocaleString()}
          </div>
        )}
      </div>

      {/* Demo Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Demo Controls</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-3">Add Test Product</h3>
            <p className="text-sm text-gray-600 mb-4">
              Add a test product to see automatic AI training in action. The AI will automatically 
              retrain within 30 seconds to include the new product in its knowledge base.
            </p>
            <button
              onClick={handleAddTestProduct}
              disabled={isAdding}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center transition-colors"
            >
              {isAdding ? (
                <RefreshCw className="animate-spin mr-2" size={16} />
              ) : (
                <Plus className="mr-2" size={16} />
              )}
              {isAdding ? 'Adding...' : 'Add Test Product'}
            </button>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-3">Clean Up Test Products</h3>
            <p className="text-sm text-gray-600 mb-4">
              Remove all test products created during this demo. The AI will automatically 
              retrain to remove these products from its knowledge base.
            </p>
            <button
              onClick={handleRemoveTestProducts}
              disabled={testProducts.length === 0}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center transition-colors"
            >
              <Trash2 className="mr-2" size={16} />
              Remove Test Products ({testProducts.length})
            </button>
          </div>
        </div>
      </div>

      {/* Current Test Products */}
      {testProducts.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Test Products</h2>
          
          <div className="space-y-3">
            {testProducts.map((product) => (
              <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">{product.name}</h4>
                  <p className="text-sm text-gray-600">{product.price} - {product.category}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Test Product
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 rounded-xl border border-blue-200 p-6">
        <h2 className="text-xl font-semibold text-blue-900 mb-4">How to Test Auto Training</h2>
        
        <div className="space-y-3 text-blue-800">
          <div className="flex items-start space-x-3">
            <span className="bg-blue-200 text-blue-900 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</span>
            <p>Click "Add Test Product" to add a new product to the database</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="bg-blue-200 text-blue-900 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</span>
            <p>Watch the "Training Sessions" counter increase automatically</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="bg-blue-200 text-blue-900 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</span>
            <p>Test the AI chat by asking about the new test product</p>
          </div>
          <div className="flex items-start space-x-3">
            <span className="bg-blue-200 text-blue-900 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</span>
            <p>Clean up by removing test products when done</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoTrainingDemo;
